import axios from "axios";
import config from "../config";

const normalizePermissionKeys = (data) => {
  if (!data) return [];

  const list = Array.isArray(data)
    ? data
    : Array.isArray(data.permissions)
      ? data.permissions
      : [];

  return list
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        return (
          item.key ||
          item.name ||
          item.permissionName ||
          item.permission ||
          item.value
        );
      }
      return null;
    })
    .filter(Boolean);
};

const rbacService = {
  // Get current user's permissions
  getMyPermissions: async () => {
    const response = await axios.get(
      `${config.apiBaseUrl}/api/Rbac/GetMyPermissions`,
      {
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      }
    );
    const payload = response.data?.data ?? response.data;
    return normalizePermissionKeys(payload?.permissions ?? payload);
  },

  // Get all roles
  getAllRoles: async () => {
    const response = await axios.get(
      `${config.apiBaseUrl}/api/Rbac/GetAllRoles`,
      {
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      }
    );
    return response.data.data;
  },

  // Get single role
  getRole: async (roleId) => {
    const response = await axios.get(
      `${config.apiBaseUrl}/api/Rbac/GetRole`,
      {
        params: { roleId },
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      }
    );
    return response.data;
  },

  // Create role
  createRole: async (roleData) => {
    const response = await axios.post(
      `${config.apiBaseUrl}/api/Rbac/CreateRole`,
      roleData,
      {
        headers: {
          "Content-Type": "application/json",
          HeaderKey: config.HeaderKey,
        },
        withCredentials: true,
      }
    );
    return response.data;
  },

  // Update role
  updateRole: async (roleData) => {
    const response = await axios.post(
      `${config.apiBaseUrl}/api/Rbac/UpdateRole`,
      roleData,
      {
        headers: {
          "Content-Type": "application/json",
          HeaderKey: config.HeaderKey,
        },
        withCredentials: true,
      }
    );
    return response.data;
  },

  // Delete role
  deleteRole: async (roleId) => {
    const response = await axios.post(
      `${config.apiBaseUrl}/api/Rbac/DeleteRole`,
      roleId,
      {
        headers: {
          "Content-Type": "application/json",
          HeaderKey: config.HeaderKey,
        },
        withCredentials: true,
      }
    );
    return response.data;
  },

  // Get all permissions
  getAllPermissions: async () => {
    const response = await axios.get(
      `${config.apiBaseUrl}/api/Rbac/GetAllPermissions`,
      {
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      }
    );
    const payload = response.data?.data ?? response.data;
    return normalizePermissionKeys(payload?.permissions ?? payload);
  },

  // Get permissions by role
  getPermissionsByRole: async (roleId) => {
    const response = await axios.get(
      `${config.apiBaseUrl}/api/Rbac/GetPermissionsByRole`,
      {
        params: { roleId },
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      }
    );
    const payload = response.data?.data ?? response.data;
    return normalizePermissionKeys(payload?.permissions ?? payload);
  },

  // Assign permissions to role
  assignPermissionsToRole: async (roleId, permissions) => {
    const response = await axios.post(
      `${config.apiBaseUrl}/api/Rbac/AssignPermissionsToRole`,
      { roleId, permissions },
      {
        headers: {
          "Content-Type": "application/json",
          HeaderKey: config.HeaderKey,
        },
        withCredentials: true,
      }
    );
    return response.data;
  },

  // Remove permissions from role
  removePermissionsFromRole: async (roleId, permissions) => {
    const response = await axios.post(
      `${config.apiBaseUrl}/api/Rbac/RemovePermissionsFromRole`,
      { roleId, permissions },
      {
        headers: {
          "Content-Type": "application/json",
          HeaderKey: config.HeaderKey,
        },
        withCredentials: true,
      }
    );
    return response.data;
  },

  // Assign role to user
  assignRoleToUser: async (userId, roleId) => {
    const response = await axios.post(
      `${config.apiBaseUrl}/api/Rbac/AssignRoleToUser`,
      { userId, roleId },
      {
        headers: {
          "Content-Type": "application/json",
          HeaderKey: config.HeaderKey,
        },
        withCredentials: true,
      }
    );
    return response.data;
  },

  // Remove role from user
  removeRoleFromUser: async (userId, roleId) => {
    const response = await axios.post(
      `${config.apiBaseUrl}/api/Rbac/RemoveRoleFromUser`,
      { userId, roleId },
      {
        headers: {
          "Content-Type": "application/json",
          HeaderKey: config.HeaderKey,
        },
        withCredentials: true,
      }
    );
    return response.data;
  },

  // Get user roles
  getUserRoles: async (userId) => {
    const response = await axios.get(
      `${config.apiBaseUrl}/api/Rbac/GetUserRoles`,
      {
        params: { userId },
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      }
    );
    return response.data.data;
  },

  // Get user effective permissions
  getUserEffectivePermissions: async (userId) => {
    const response = await axios.get(
      `${config.apiBaseUrl}/api/Rbac/GetUserEffectivePermissions`,
      {
        params: { userId },
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      }
    );
    return response.data;
  },
};

export default rbacService;
